import { IMilestoneApprover, INotificationRole } from "@/interfaces";

export type TestRole = {
  roleId: string;
  region: string;
  functionalArea: string;
  canSetNotApplicable?: boolean;
  defaultForProductCategories?: string[];
};

export const isValidRole = (
  roles:
    | (IMilestoneApprover & { defaultForProductCategories?: string[] })[]
    | (INotificationRole & { defaultForProductCategories?: string[] })[]
    | TestRole[]
): boolean => {
  if (!Array.isArray(roles) || roles.length === 0) return false;

  const seenFullKeys = new Set<string>();
  const categoryTracker = new Map<string, Set<string>>();
  const baseKeyWithEmptyCategories = new Set<string>();
  const applicabilityCount = new Map<string, Set<boolean>>(); // Tracks canSetNotApplicable seen for each role-region-functionalArea

  for (const role of roles) {
    const {
      roleId,
      region,
      functionalArea,
      canSetNotApplicable = false,
      defaultForProductCategories = [],
    } = role;

    if (!roleId || !region || !functionalArea) {
      return false;
    }

    const groupKey = `${roleId}|${region}|${functionalArea}`;
    const baseKey = groupKey;
    const sortedCategories = [...new Set(defaultForProductCategories)].sort();

    const seenApplicabilities =
      applicabilityCount.get(groupKey) ?? new Set<boolean>();
    if (seenApplicabilities.has(canSetNotApplicable)) {
      return false;
    }
    seenApplicabilities.add(canSetNotApplicable);
    applicabilityCount.set(groupKey, seenApplicabilities);

    if (
      baseKeyWithEmptyCategories.has(baseKey) &&
      sortedCategories.length > 0
    ) {
      return false;
    }

    if (sortedCategories.length === 0) {
      if (categoryTracker.has(baseKey)) {
        return false;
      }
      baseKeyWithEmptyCategories.add(baseKey);
    }

    const usedCategories = categoryTracker.get(baseKey) ?? new Set<string>();
    for (const category of sortedCategories) {
      if (usedCategories.has(category)) {
        return false;
      }
    }

    sortedCategories.forEach((cat) => usedCategories.add(cat));
    categoryTracker.set(baseKey, usedCategories);

    const fullKey = `${groupKey}|${canSetNotApplicable}|${sortedCategories.join(
      ","
    )}`;
    if (seenFullKeys.has(fullKey)) {
      return false;
    }

    seenFullKeys.add(fullKey);
  }

  return true;
};
